import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Copy, FileDown, Loader2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { setOrbState } from "@/lib/orb";

export const OcrView = ({ active }) => {
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const input = useRef(null);

  const pick = (e) => {
    const list = Array.from(e.target.files || []).filter((f) => /image\/(jpeg|png|webp)/.test(f.type));
    if (list.length < (e.target.files?.length || 0)) toast.message("Alcuni file sono stati ignorati: usa JPG, PNG o WEBP.");
    setFiles((x) => [...x, ...list].slice(0, 15));
    e.target.value = "";
  };

  const run = async () => {
    setBusy(true);
    setOrbState("elaborazione");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/ocr", fd);
      setText(data.pages.map((p) => (data.pages.length > 1 ? `--- Pagina ${p.page} ---\n${p.text}` : p.text)).join("\n\n"));
      toast.success(`Testo estratto da ${data.pages.length} pagina/e`);
    } catch (e) { toast.error(errMsg(e, "OCR non riuscito")); }
    setOrbState("idle");
    setBusy(false);
  };

  const pdf = async () => {
    setPdfBusy(true);
    try {
      const { data } = await api.post("/ocr/pdf", { title: "Documento OCR - Aurora ON", text }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "aurora-ocr.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Esportazione PDF non riuscita"); }
    setPdfBusy(false);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6" data-testid="ocr-view">
      <p className="hud-tag mb-2">Scanner multi-pagina</p>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">OCR · Estrazione testo</h2>
      <div className="hud-card p-5">
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple capture="environment" hidden onChange={pick} data-testid="ocr-file-input" />
        <button onClick={() => input.current.click()} disabled={!active} data-testid="ocr-upload-btn"
          className="w-full border border-dashed border-cyan-400/40 rounded-xl py-8 flex flex-col items-center gap-2 text-slate-300 hover:border-amber-300/60 hover:bg-cyan-500/5 transition-colors disabled:opacity-50">
          <Upload className="text-cyan-300" /> <span className="text-sm">Carica o scatta le pagine (fino a 15 immagini)</span>
        </button>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="ocr-file-list">
            {files.map((f, i) => (
              <span key={`${f.name}-${f.size}-${f.lastModified}`} className="text-xs font-mono bg-[#050b18] border border-cyan-500/20 rounded-full pl-3 pr-1.5 py-1 flex items-center gap-1.5">
                {i + 1}. {f.name.slice(0, 22)}
                <button onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400"><X size={12} /></button>
              </span>
            ))}
          </div>
        )}
        <button onClick={run} disabled={!files.length || busy || !active} data-testid="ocr-extract-btn" className="btn-aurora mt-4 rounded-xl px-6 py-3 text-sm uppercase flex items-center gap-2 disabled:opacity-50">
          {busy && <Loader2 className="animate-spin" size={16} />} Estrai testo
        </button>
      </div>
      {text && (
        <div className="hud-card p-5 mt-5">
          <div className="flex flex-wrap gap-2 mb-3">
            <button onClick={() => { navigator.clipboard.writeText(text); toast.success("Testo copiato"); }} data-testid="ocr-copy-btn" className="btn-ghost-hud rounded-full px-4 py-2 text-xs flex items-center gap-2"><Copy size={14} /> Copia</button>
            <button onClick={pdf} disabled={pdfBusy} data-testid="ocr-pdf-btn" className="btn-ghost-hud rounded-full px-4 py-2 text-xs flex items-center gap-2">{pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Esporta PDF</button>
          </div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={16} data-testid="ocr-result-text"
            className="w-full bg-[#050b18]/80 border border-cyan-500/20 rounded-xl p-4 text-sm font-mono text-slate-200 outline-none" />
        </div>
      )}
    </div>
  );
};
